// FaltaComprar — pagina /cotacoes/falta-comprar (DA-368 Fase 2).
// Junta os itens de TODAS as cotacoes abertas numa lista so, UMA linha por produto (deduplica:
// soma a Qtd, junta os numeros das cotacoes). Filtro "Mostrar ja comprados" (padrao OFF — os que
// ja foram marcados na celula da cotacao ficam ocultos, ja estao a caminho). SO-LEITURA: a marca
// de "comprei" continua na celula produto x fornecedor da cotacao (DA-368); aqui clicar na linha
// (ou no chip da cotacao) apenas ABRE a cotacao pra marcar la.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, ShoppingCart, ImageOff } from 'lucide-react';
import { api } from '../../lib/api';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { FotoProduto } from '../../components/FotoProduto';
import { formatDataSP } from '@fiodecobre/shared';

interface CotacaoRef {
  id: number;
  numero: string;
  tipo: string;
}

interface Linha {
  produto_id: number | null;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  marca_nome: string | null;
  grupo_nome: string | null;
  unidade_sigla: string | null;
  qtd_total: string;
  estoque_atual: string | null;
  estoque_minimo: string | null;
  compra_em_aberto: boolean;
  ultima_compra_em: string | null;
  ultima_compra_fornecedor_nome: string | null;
  cotacoes: CotacaoRef[];
}

const CHAVE_MOSTRAR = 'faltaComprar:mostrarComprados';
// Passado no navigate pra o botao Voltar da cotacao devolver pra ca (nao pra lista de Cotacoes).
const VOLTA_PARA = { from: '/cotacoes/falta-comprar' };
// Mesma decisao da CotacoesHome (rotaDaCotacao): informativa abre na lista chapada.
const rotaCotacao = (c: CotacaoRef) =>
  c.tipo === 'informativa' ? `/cotacoes/${c.id}/informativa` : `/cotacoes/${c.id}/respostas`;

const fmtQtd = (v: string | null) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 3 });

export function FaltaComprar() {
  const navigate = useNavigate();
  const sticky = useStickyHeader();
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [mostrarComprados, setMostrarComprados] = useState<boolean>(
    () => localStorage.getItem(CHAVE_MOSTRAR) === '1',
  );

  useEffect(() => {
    localStorage.setItem(CHAVE_MOSTRAR, mostrarComprados ? '1' : '0');
  }, [mostrarComprados]);

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    api
      .get<Linha[]>('/api/lista-compras/falta-comprar')
      .then((r) => {
        if (vivo) setLinhas(r);
      })
      .finally(() => {
        if (vivo) setLoading(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return linhas.filter((l) => {
      if (!mostrarComprados && l.compra_em_aberto) return false;
      if (!q) return true;
      return (
        l.descricao.toLowerCase().includes(q) ||
        (l.codigo_interno ?? '').toLowerCase().includes(q) ||
        (l.marca_nome ?? '').toLowerCase().includes(q)
      );
    });
  }, [linhas, busca, mostrarComprados]);

  const nComprar = useMemo(() => linhas.filter((l) => !l.compra_em_aberto).length, [linhas]);
  const nComprados = linhas.length - nComprar;
  const nCotacoes = useMemo(() => {
    const ids = new Set<number>();
    linhas.forEach((l) => l.cotacoes.forEach((c) => ids.add(c.id)));
    return ids.size;
  }, [linhas]);

  return (
    <div className="modulo-pagina">
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button className="btn-icon" onClick={() => navigate('/cotacoes')} title="Voltar">
              <ArrowLeft size={20} />
            </button>
            <ShoppingCart size={20} style={{ display: 'inline' }} /> O que falta comprar
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', marginLeft: 4 }}>
              · {nComprar} a comprar
              {mostrarComprados && nComprados > 0 ? ` + ${nComprados} comprados` : ''}
              {nCotacoes > 0 ? ` · ${nCotacoes} cotaç${nCotacoes === 1 ? 'ão' : 'ões'} aberta${nCotacoes === 1 ? '' : 's'}` : ''}
            </span>
          </h2>
          <div className="pagina-acoes">
            <div style={{ position: 'relative' }}>
              <Search size={15} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar produto…"
                style={{ paddingLeft: 30, width: 220 }}
              />
            </div>
            <label
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}
              title="Produtos ja marcados como comprados na cotacao (a caminho) ficam ocultos por padrao"
            >
              <input type="checkbox" checked={mostrarComprados} onChange={(e) => setMostrarComprados(e.target.checked)} />
              Mostrar já comprados
            </label>
          </div>
        </div>
      </div>

      <div style={{ padding: '12px 16px 16px' }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
        ) : filtradas.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            <ShoppingCart size={40} style={{ margin: '0 auto 8px', display: 'block', color: 'var(--border)' }} />
            {linhas.length === 0 ? 'Nenhuma cotação aberta com itens' : 'Nenhum produto encontrado'}
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: 13, ...sticky.tableStyle }}>
            <thead>
              <tr>
                <th style={{ ...sticky.thStyle, padding: 8, textAlign: 'center', width: 56, fontWeight: 600 }}>Foto</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', width: 64, fontWeight: 600 }}>Cod.</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Descrição</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'center', width: 56, fontWeight: 600 }}>Un.</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'right', width: 72, fontWeight: 600 }}>Qtd</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', width: 150, fontWeight: 600 }}>Cotação</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'right', width: 80, fontWeight: 600 }}>Estoque</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', width: 150, fontWeight: 600 }}>Última compra</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map((l, idx) => {
                const primeira = l.cotacoes[0];
                const alerta =
                  l.estoque_atual != null &&
                  l.estoque_minimo != null &&
                  Number(l.estoque_atual) <= Number(l.estoque_minimo);
                return (
                  <tr
                    key={l.produto_id ?? `sku-${idx}`}
                    style={{ borderBottom: '1px solid var(--border)', cursor: primeira ? 'pointer' : 'default' }}
                    onClick={() => primeira && navigate(rotaCotacao(primeira), { state: VOLTA_PARA })}
                  >
                    <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                      {l.foto_url ? (
                        <FotoProduto url={l.foto_url} tam={46} style={{ display: 'inline-block' }} />
                      ) : (
                        <div style={{ width: 46, height: 46, background: 'var(--grid-row-alt-bg)', borderRadius: 4, border: '1px solid var(--border)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <ImageOff size={16} color="#d1d5db" />
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--page-fg)' }}>{l.codigo_interno ?? '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 500, color: 'var(--page-fg)' }}>
                        {l.descricao}
                        {l.compra_em_aberto && (
                          <span
                            title={`Comprado${l.ultima_compra_em ? ` ${formatDataSP(l.ultima_compra_em)}` : ''}${l.ultima_compra_fornecedor_nome ? ` · ${l.ultima_compra_fornecedor_nome}` : ''} — a caminho`}
                            style={{ display: 'inline-block', marginLeft: 6, backgroundColor: '#fde68a', color: '#000', padding: '1px 6px', borderRadius: 9999, fontSize: 10, fontWeight: 700, verticalAlign: 'middle', whiteSpace: 'nowrap' }}
                          >
                            🛒 a caminho
                          </span>
                        )}
                      </div>
                      {(l.marca_nome || l.grupo_nome) && (
                        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                          {[l.marca_nome, l.grupo_nome].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--text-muted)' }}>{l.unidade_sigla ?? '—'}</td>
                    <td className="cel-num" style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--page-fg)' }}>{fmtQtd(l.qtd_total)}</td>
                    <td style={{ padding: '10px 12px' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {l.cotacoes
                          .slice()
                          .sort((a, b) => a.numero.localeCompare(b.numero))
                          .map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => navigate(rotaCotacao(c), { state: VOLTA_PARA })}
                              title={`Abrir ${c.numero}`}
                              style={{ padding: '2px 7px', fontSize: 11, fontWeight: 700, borderRadius: 4, background: 'var(--primary)', color: 'var(--accent-fg)', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              {c.numero}
                            </button>
                          ))}
                      </div>
                    </td>
                    <td className="cel-num" style={{ padding: '10px 12px', textAlign: 'right', color: alerta ? '#dc2626' : 'var(--page-fg)', fontWeight: alerta ? 700 : 500 }}>
                      {alerta && <span title="Estoque no minimo ou abaixo" style={{ color: '#d97706' }}>▲ </span>}
                      {l.estoque_atual == null ? '—' : Number(l.estoque_atual).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {l.ultima_compra_em ? (
                        <span
                          title={l.ultima_compra_fornecedor_nome ?? undefined}
                          style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.15, background: 'var(--primary)', color: 'var(--accent-fg)', fontWeight: 700, borderRadius: 12, padding: '3px 10px', fontSize: 11.5, whiteSpace: 'nowrap' }}
                        >
                          {formatDataSP(l.ultima_compra_em)}
                          {l.ultima_compra_fornecedor_nome && (
                            <span style={{ fontWeight: 600, opacity: 0.85, fontSize: 10 }}>{l.ultima_compra_fornecedor_nome}</span>
                          )}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </div>
    </div>
  );
}
